<template>
  <div class="nebSimpleRangeSelect"
    v-neb-click-outside="onClickOutside"
  >
    <div class="nebSimpleRangeSelect-select"
      @click="toggleShowDropdown"
      :class="{
        placeholder: !valueSelected,
        disabled,
        readOnly,
        open: showDropdown,
        dirty: isDirty
      }"
    >
      <span>{{ valueLabel }}</span>

      <neb-icon name="caretDown"
        height="12px"
        width="12px"
        v-show="showDownCaret"
      />

      <neb-icon name="x"
        height="12px"
        width="12px"
        v-show="showClearControl"
        @click.native="onClear"
      />
    </div>

    <div class="nebSimpleRangeSelect-dropdown"
      v-show="showDropdown"
      ref="dropdown"
    >
      <range-dropdown :lowerValue="localValue[0]"
        :upperValue="localValue[1]"
        :min="min"
        :max="max"
        :avg="avg"
        :showMin="showMin"
        :showMax="showMax"
        :showAvg="showAvg"
        :loading="loading"
        :valueType="valueType"
        @save:value="onValueChange"
      />
    </div>
  </div>
</template>

<script>
import { isEqual } from 'lodash'
import NebIcon from '../../Icon'
import RangeDropdown from './Dropdown.vue'

const formatValue = (v, type) => {
  switch (type) {
    case 'int':
      return v.toLocaleString()
    case 'percent':
      return `${v}%`
    case 'seconds':
      return `${v}s`
    default: return v
  }
}

export default {
  name: 'NebSimpleRangeSelect',

  props: {
    value: {
      type: Array,
      default: () => []
    },
    placeholder: {
      type: String,
      default: 'Enter Value'
    },
    showMin: {
      type: Boolean,
      default: false
    },
    showMax: {
      type: Boolean,
      default: false
    },
    showAvg: {
      type: Boolean,
      default: false
    },
    min: {
      type: Number,
      default: undefined
    },
    max: {
      type: Number,
      default: undefined
    },
    avg: {
      type: Number,
      default: undefined
    },
    prefix: {
      type: String,
      default: ''
    },
    loading: {
      type: Boolean,
      default: false
    },
    highlightDirty: {
      type: Boolean,
      default: false
    },
    defaultValue: {
      type: Array,
      default: () => []
    },
    disabled: {
      type: Boolean,
      default: false
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    clearable: {
      type: Boolean,
      default: false
    },
    // Options are: int, percent, seconds
    valueType: {
      type: String,
      default: 'int'
    }
  },

  components: {
    NebIcon,
    RangeDropdown
  },

  data: () => ({
    showDropdown: false
  }),

  computed: {
    valueSelected () {
      return this.value.length > 0
    },
    isDirty () {
      return this.highlightDirty &&
        this.defaultValue !== undefined &&
        !isEqual(this.value, this.defaultValue)
    },
    localValue: {
      get () { return this.value },
      set (v) { this.$emit('input', v) }
    },
    valueLabel () {
      const [ min, max ] = this.localValue

      let minval = min,
        maxval = max

      if (!min && !max) {
        return this.prefix
          ? `${this.prefix}: ${this.placeholder}`
          : this.placeholder
      }

      if (min === null) {
        minval = '-inf'
      }

      if (max === null) {
        maxval = 'inf'
      }

      return this.prefix
        ? `${this.prefix}: ${formatValue(minval, this.valueType)} - ${formatValue(maxval, this.valueType)}`
        : `${formatValue(minval, this.valueType)} - ${formatValue(maxval, this.valueType)}`
    },
    showDownCaret () {
      return this.clearable
        ? !this.readOnly && (!this.valueSelected || this.value === this.defaultValue)
        : !this.readOnly
    },
    showClearControl () {
      return this.clearable
        && !this.readOnly
        && !this.disabled
        && this.valueSelected
        && this.value !== this.defaultValue
    }
  },

  methods: {
    onClickOutside () {
      this.showDropdown = false
    },
    toggleShowDropdown () {
      if (this.disabled || this.readOnly) { return }

      if (!this.showDropdown) {
        this.$emit('action:openDropdown')
      } else {
        this.$emit('action:closeDropdown')
      }

      this.showDropdown = !this.showDropdown
    },
    onValueChange (v) {
      this.toggleShowDropdown()
      this.localValue = v
    },
    onClear (e) {
      e.stopPropagation()
      this.localValue = this.defaultValue
      this.showDropdown = false
    }
  }
}
</script>

<style lang="scss">
@import '../../style.scss';

.nebSimpleRangeSelect {
  height: 34px;
  position: relative;
  width: 100%;

  &-select {
    height: 34px;
    background-color: $white;
    border-radius: 3px;
    border: solid 1px $light-grey-blue;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding-left: 15px;
    padding-right: 15px;
    padding-top: 1px;
    color: $slate-grey;
    font-family: $defaultFont;
    font-size: 12px;
    cursor: pointer;
    position: relative;

    span {
      margin-right: 10px;
      white-space: nowrap;
      overflow: hidden;
    }

    .nebIcon {
      color: $slate-grey;
      opacity: 0.7;
    }

    &.placeeholder {
      color: $slate-grey;
    }

    &.disabled {
      color: $pinkish-grey;
      background-color: $white-four;
      cursor: not-allowed;

      .nebIcon {
        color: $warm-grey;
      }
    }

    &.readOnly {
      border-top: none;
      border-left: none;
      border-right: none;
      border-bottom: 2px solid $warm-grey;
      cursor: initial;
      border-radius: initial;
    }

    &.open {
      box-shadow: 0 0 3px 0 $tiffany-blue;
      border: solid 1px $tiffany-blue;
    }

    &.dirty {
      background-color: #e8f4fc;
      border: 1px solid #a0c3d2;
    }
  }

  &-dropdown {
    background-color: $white;
    border-radius: 3px;
    border: solid 1px $pinkish-grey;
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.5);
    position: absolute;
    top: 39px;
    box-sizing: border-box;
    overflow: hidden;
    z-index: 2001;
    padding: 20px;

    &.edge {
      right: 0;
    }
  }
}
</style>
